import axios from 'axios';

const getApiBaseUrl = (): string => {
  // Get API URL from window.ENV (loaded from config.js)
  const apiUrl = window.ENV?.VITE_API_BASE_URL;

  if (!apiUrl) {
    console.error('⚠️ window.ENV.VITE_API_BASE_URL is not set in authService! Check that config.js is loaded properly.');
    console.error('Current window.ENV:', window.ENV);
    // Use an obviously broken URL so it's immediately clear the config failed to load
    return 'http://config.not.loaded.xxx';
  }

  return apiUrl;
};

const API_BASE_URL = getApiBaseUrl();

export interface AuthResponse {
  success: boolean;
  message?: string;
  error?: string;
  accessToken?: string;
  refreshToken?: string;
  sessionToken?: string;
  expiresIn?: number;
  sessionExpiresAt?: string;
  expiresAt?: string;
  user?: {
    id: string;
    email: string | null;
    name: string;
    isAnonymous: boolean;
    roles: string[];
    permissions: string[];
  };
}

export interface User {
  id: string;
  email: string | null;
  name: string;
  isAnonymous: boolean;
  roles: Array<{ id: string; name: string; description: string }>;
  permissions: string[];
  mobile_number?: string | null;
}

export const authService = {
  /**
   * Request OTP code for email login
   */
  requestOTP: async (email: string): Promise<AuthResponse> => {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/otp/request`,
      { email }
    );
    return response.data;
  },

  /**
   * Verify OTP code and login
   */
  verifyOTP: async (email: string, code: string): Promise<AuthResponse> => {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/otp/verify`,
      { email, otpCode: code }
    );

    const data = response.data;

    // Store tokens in localStorage
    if (data.success && data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);
      localStorage.setItem('refreshToken', data.refreshToken);

      // Store session expiry time for sliding window tracking
      if (data.sessionExpiresAt) {
        localStorage.setItem('sessionExpiresAt', data.sessionExpiresAt);
      }

      // Store token expiry time (15 minutes from now)
      const tokenExpiresAt = new Date(Date.now() + (data.expiresIn || 900) * 1000).toISOString();
      localStorage.setItem('tokenExpiresAt', tokenExpiresAt);

      // Remove anonymous token if exists
      localStorage.removeItem('anonymousSessionToken');
      localStorage.removeItem('anonymousExpiresAt');
    }

    return data;
  },

  /**
   * Create anonymous session
   */
  createAnonymousSession: async (): Promise<AuthResponse> => {
    const response = await axios.post(
      `${API_BASE_URL}/api/auth/anonymous/session`
    );

    const data = response.data;

    // Store anonymous session token
    if (data.success && data.sessionToken) {
      localStorage.setItem('anonymousSessionToken', data.sessionToken);

      // Store anonymous session expiry time
      if (data.expiresAt) {
        localStorage.setItem('anonymousExpiresAt', data.expiresAt);
      }

      // Remove authenticated tokens if exist
      localStorage.removeItem('accessToken');
      localStorage.removeItem('refreshToken');
      localStorage.removeItem('sessionExpiresAt');
      localStorage.removeItem('tokenExpiresAt');
    }

    return data;
  },

  /**
   * Get current user info
   * Automatically attempts token refresh if access token is expired
   */
  getCurrentUser: async (): Promise<User> => {
    let token = localStorage.getItem('accessToken');
    const anonymousToken = localStorage.getItem('anonymousSessionToken');
    const refreshToken = localStorage.getItem('refreshToken');

    // Check if JWT token might be expired and we have a refresh token
    if (token && refreshToken) {
      const tokenExpiresAt = localStorage.getItem('tokenExpiresAt');
      if (tokenExpiresAt) {
        const expiryTime = new Date(tokenExpiresAt).getTime();
        const now = Date.now();
        // If token expires in less than 30 seconds, proactively refresh
        if (expiryTime - now < 30000) {
          try {
            const refreshResult = await authService.refreshToken();
            if (refreshResult.success && refreshResult.accessToken) {
              token = refreshResult.accessToken;
            }
          } catch (refreshError) {
            console.warn('Proactive token refresh failed:', refreshError);
            // Continue with existing token, let the request fail if truly expired
          }
        }
      }
    }

    const headers: any = {};
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    } else if (anonymousToken) {
      headers['x-anonymous-session'] = anonymousToken;
    } else {
      throw new Error('No authentication token found');
    }

    try {
      const response = await axios.get(
        `${API_BASE_URL}/api/general/me`,
        { headers }
      );
      // Backend returns user data directly, not nested under 'user' property
      return response.data;
    } catch (error: any) {
      // If we get a 401 and have a refresh token, try to refresh and retry
      if (error.response?.status === 401 && refreshToken && token) {
        try {
          const refreshResult = await authService.refreshToken();
          if (refreshResult.success && refreshResult.accessToken) {
            // Retry with new token
            const retryResponse = await axios.get(
              `${API_BASE_URL}/api/general/me`,
              { headers: { Authorization: `Bearer ${refreshResult.accessToken}` } }
            );
            return retryResponse.data;
          }
        } catch (refreshError) {
          console.error('Token refresh failed:', refreshError);
        }
      }
      // Re-throw the original error if refresh didn't help
      throw error;
    }
  },

  /**
   * Refresh access token
   */
  refreshToken: async (): Promise<AuthResponse> => {
    const refreshToken = localStorage.getItem('refreshToken');

    if (!refreshToken) {
      throw new Error('No refresh token found');
    }

    const response = await axios.post(
      `${API_BASE_URL}/api/auth/token/refresh`,
      { refreshToken }
    );

    const data = response.data;

    // Update access token and expiry times
    if (data.success && data.accessToken) {
      localStorage.setItem('accessToken', data.accessToken);

      // Update session expiry time (sliding window)
      if (data.sessionExpiresAt) {
        localStorage.setItem('sessionExpiresAt', data.sessionExpiresAt);
      }

      // Update token expiry time (15 minutes from now)
      const tokenExpiresAt = new Date(Date.now() + (data.expiresIn || 900) * 1000).toISOString();
      localStorage.setItem('tokenExpiresAt', tokenExpiresAt);
    }

    return data;
  },

  /**
   * Logout user
   */
  logout: async (): Promise<void> => {
    const token = localStorage.getItem('accessToken');

    if (token) {
      try {
        await axios.post(
          `${API_BASE_URL}/api/auth/logout`,
          {},
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } catch (error) {
        console.error('Logout error:', error);
      }
    }

    // Clear all tokens and expiry times
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('anonymousSessionToken');
    localStorage.removeItem('sessionExpiresAt');
    localStorage.removeItem('tokenExpiresAt');
    localStorage.removeItem('anonymousExpiresAt');
  },

  /**
   * Check if user is authenticated (either JWT or anonymous)
   */
  isAuthenticated: (): boolean => {
    return !!(
      localStorage.getItem('accessToken') ||
      localStorage.getItem('anonymousSessionToken')
    );
  },

  /**
   * Check if user is anonymous
   */
  isAnonymous: (): boolean => {
    return !!(localStorage.getItem('anonymousSessionToken'));
  }
};
